import React from 'react';
import dayjs from 'dayjs';
import Box from '@mui/material/Box/Box';
import Stack from '@mui/material/Stack/Stack';
import Typography from '@mui/material/Typography/Typography';
import Button from '@mui/material/Button/Button';
import Avatar from '@mui/material/Avatar/Avatar';
import AvatarGroup from '@mui/material/AvatarGroup/AvatarGroup';
import { alpha, getContrastRatio, useTheme } from '@mui/material/styles';
import { HackathonStatus } from '../../utils/hackathonStatus';
import { LOOK } from '../../theme';

export interface TeamPrompt {
    title: string;
    subtitle: string;
    actionLabel: string;
    onAction: () => void;
}

const timeLeft = (deadline: Date) => {
    const hours = dayjs(deadline).diff(dayjs(), 'hour');
    if (hours < 1) {
        return `${Math.max(dayjs(deadline).diff(dayjs(), 'minute'), 0)}m`;
    }
    return hours < 48 ? `${hours}h` : `${Math.floor(hours / 24)}d`;
}

// cardboard and terminal tints, from the icon
const AVATAR_COLORS = [LOOK.kraft, '#c3efcb', '#f1d6b4', '#dce9d2', '#eebd94', '#e9e1d3'];

/** One of the big numbers under the hero: a small label, the figure, and a line of detail. */
const Figure = ({ label, value, detail, compact, children }: { label: string, value: React.ReactNode, detail?: string, compact?: boolean, children?: React.ReactNode }) => {
    const { mono } = useTheme().brand;
    return (
    // a soft tile, not a rule along one edge
    <Box sx={{ backgroundColor: 'var(--surface-muted)', borderRadius: compact ? '14px' : '18px', px: compact ? 1.5 : 2.25, py: compact ? 1.25 : 1.75, minWidth: 0 }}>
        <Typography variant="overline" color="text.secondary" component="div" sx={{ lineHeight: 1.6 }}>{label}</Typography>
        <Stack direction="row" spacing={1.5} alignItems="center" useFlexGap flexWrap="wrap">
            {/* figures read like a terminal readout */}
            <Typography variant="h1" component="p" sx={{ fontFamily: mono, fontWeight: 600, letterSpacing: '-0.04em', fontSize: compact ? '2rem' : { xs: '2.5rem', md: '3.5rem' }, lineHeight: 1 }}>{value}</Typography>
            {children}
        </Stack>
        {detail && <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{detail}</Typography>}
    </Box>
    );
};

/**
 * The top of a hackathon's page: its name, very large, on the event's banner image, its brand colour, or the look's kraft
 * graph paper; then the figures that matter (deadline, hackers, ideas) and, for someone not yet in a team, what to do next.
 * `compact` keeps it small and stacked at any width, for narrow spots like the branding preview.
 */
export const HackathonBanner = ({
    name,
    organizationName,
    status,
    dateRange,
    deadline,
    people,
    ideaCount,
    teamPrompt,
    actions,
    compact
}: {
    name: string,
    organizationName?: string,
    status: HackathonStatus,
    dateRange?: string,
    deadline: Date | null,
    people: Array<{ _id?: string, username: string, email: string }>,
    ideaCount: number,
    teamPrompt?: TeamPrompt | null,
    actions?: React.ReactNode,
    compact?: boolean
}) => {
    const theme = useTheme();
    const { rawPrimary, bannerUrl, logoUrl, hasColor, mono } = theme.brand;
    // a responsive value that stays on its phone setting when compact
    const wide = <T,>(xs: T, md: T) => compact ? xs : { xs, md };

    // what the hero sits on, and whether its text is ink (light ground) or white
    const onColor = !bannerUrl && hasColor;
    const lightGround = !bannerUrl && (!onColor || getContrastRatio('#ffffff', rawPrimary) < 3);
    const fg = lightGround ? LOOK.ink : '#ffffff';
    const background = bannerUrl
        ? `linear-gradient(180deg, rgba(0,0,0,0.12), rgba(0,0,0,0.58)), url("${bannerUrl.replace(/"/g, '%22')}") center / cover no-repeat`
        : onColor ? rawPrimary : `${LOOK.grain}, ${LOOK.heroPanel}`;

    const deadlineOpen = !!deadline && dayjs(deadline).isAfter(dayjs());

    return (
        <Stack spacing={wide(2.5, 3.5)}>
            <Box sx={{
                position: 'relative',
                overflow: 'hidden',
                borderRadius: wide('22px', '32px'),
                background,
                color: fg,
                px: wide(2.5, 6),
                pt: wide(2.5, 4),
                pb: wide(3, 5.5),
                minHeight: wide(220, 380),
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                gap: 3
            }}>
                <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minHeight: 36 }}>
                    {logoUrl && <Box component="img" src={logoUrl} alt="" sx={{ height: 36, maxWidth: 140, objectFit: 'contain', borderRadius: '8px', display: 'block' }} />}
                    {organizationName && <Typography sx={{ fontSize: '1.05rem', opacity: 0.85 }}>{organizationName}</Typography>}
                </Stack>
                <Stack spacing={wide(1.25, 2)}>
                    {/* the look's tag: a thin outline, a dot for the phase */}
                    <Box sx={{
                        alignSelf: 'flex-start', display: 'flex', alignItems: 'center', gap: 1,
                        border: `1px solid ${alpha(fg, 0.55)}`, borderRadius: 999, px: 1.5, py: 0.5,
                        fontFamily: mono, fontSize: '0.85rem', fontWeight: 500, backdropFilter: 'blur(2px)'
                    }}>
                        <Box component="span" sx={{ width: 7, height: 7, borderRadius: '50%', backgroundColor: 'currentColor' }} />
                        {status.label}
                    </Box>
                    <Typography variant="h1" component="h1" sx={{
                        color: fg,
                        fontSize: wide('2.5rem', '5rem'),
                        lineHeight: 0.95,
                        letterSpacing: '-0.045em',
                        maxWidth: 980,
                        overflowWrap: 'anywhere'
                    }}>{name}</Typography>
                    {dateRange && <Typography sx={{ fontSize: wide('1.05rem', '1.25rem'), opacity: 0.85 }}>{dateRange}</Typography>}
                </Stack>
            </Box>

            {/* as many columns as figures: without a deadline there are two */}
            <Box sx={{ display: 'grid', gap: wide(2, 4), gridTemplateColumns: `repeat(${deadline ? 3 : 2}, minmax(0, 1fr))` }}>
                {
                    deadline && <Figure compact={compact}
                        label={deadlineOpen ? 'Ideas close in' : 'Ideas closed'}
                        value={deadlineOpen ? timeLeft(deadline) : dayjs(deadline).format('MMM D')}
                        detail={deadlineOpen ? dayjs(deadline).format('dddd, MMM D') : undefined} />
                }
                <Figure compact={compact} label="Hackers" value={people.length}>
                    {people.length > 0 && <AvatarGroup max={5} sx={{
                        '& .MuiAvatar-root': { width: 30, height: 30, fontSize: '0.8rem', borderColor: LOOK.cream, color: LOOK.ink, fontWeight: 600 }
                    }}>
                        {people.map((person, index) => (
                            <Avatar key={person._id || person.email} sx={{ bgcolor: AVATAR_COLORS[index % AVATAR_COLORS.length] }}>
                                {(person.username || person.email || '?').charAt(0).toUpperCase()}
                            </Avatar>
                        ))}
                    </AvatarGroup>}
                </Figure>
                <Figure compact={compact} label="Ideas" value={ideaCount} />
            </Box>

            {
                teamPrompt && (
                    // a live moment: a terminal-black panel with a green glow
                    <Box sx={{
                        borderRadius: wide('18px', '24px'),
                        color: LOOK.cream,
                        background: `radial-gradient(120% 160% at 100% 0%, ${alpha(LOOK.green, 0.22)}, transparent 55%), ${LOOK.terminal}`,
                        boxShadow: `inset 0 0 0 1px ${alpha(LOOK.green, 0.18)}`,
                        px: wide(2, 3.5),
                        py: wide(2, 2.75),
                        display: 'flex',
                        flexDirection: wide('column', 'row'),
                        alignItems: wide('flex-start', 'center'),
                        justifyContent: 'space-between',
                        gap: 2
                    }}>
                        <Stack direction="row" spacing={1.75} alignItems="center" sx={{ minWidth: 0 }}>
                            <Box sx={{ width: 10, height: 10, flexShrink: 0, borderRadius: '50%', backgroundColor: LOOK.green, boxShadow: `0 0 12px 2px ${alpha(LOOK.green, 0.7)}` }} />
                            <Box sx={{ minWidth: 0 }}>
                                <Typography variant="h4" component="p" sx={{ color: LOOK.cream }}>{teamPrompt.title}</Typography>
                                <Typography sx={{ color: alpha(LOOK.cream, 0.75) }}>{teamPrompt.subtitle}</Typography>
                            </Box>
                        </Stack>
                        <Button onClick={teamPrompt.onAction} sx={{
                            flexShrink: 0, minHeight: 44, px: 3,
                            backgroundColor: LOOK.green, color: LOOK.terminal,
                            '&:hover': { backgroundColor: alpha(LOOK.green, 0.85) }
                        }}>{teamPrompt.actionLabel}</Button>
                    </Box>
                )
            }

            {actions && <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>{actions}</Stack>}
        </Stack>
    );
};
